import { useCallback, useEffect, useRef, useState } from "react";
import { listarMatriculas, obtenerMatricula } from "../../../../services/matriculasService";
import { normalizeMatricula } from "../adapters/matriculasAdapter";
import { showToast } from "../../../../utils/showToast";
import { getSocket, initSocket } from "../../../../utils/socketClient";

const initialFilters = {
  search: "",
  estado_matricula: "all",
  estado_pago: "all",
  activo: "all",
  fecha_desde: "",
  fecha_hasta: "",
};

export default function useMatriculasList() {
  const [matriculas, setMatriculas] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState({
    total: 0,
    activas: 0,
    suspendidas: 0,
    finalizadas: 0,
    anuladas: 0,
    pendientesPago: 0,
    pagoParcial: 0,
    pagadas: 0,
  });

  const fetchMatriculas = useCallback(async (pageOverride, filtrosOverride) => {
    setLoading(true);
    const currentPage = pageOverride ?? page;
    const currentFiltros = filtrosOverride ?? filtros;
    try {
      const params = {
        pagina: currentPage,
        porPagina: pageSize,
        search: currentFiltros.search || undefined,
        estado_matricula: currentFiltros.estado_matricula !== 'all' ? currentFiltros.estado_matricula : undefined,
        estado_pago: currentFiltros.estado_pago !== 'all' ? currentFiltros.estado_pago : undefined,
        activo: currentFiltros.activo !== 'all' ? currentFiltros.activo : undefined,
        fecha_desde: currentFiltros.fecha_desde || undefined,
        fecha_hasta: currentFiltros.fecha_hasta || undefined,
      };

      const response = await listarMatriculas(params);
      const parsed = (Array.isArray(response?.data) ? response.data : []).map(normalizeMatricula);
      setMatriculas(parsed);
      setTotal(Number(response?.total || 0));

      const srv = response?.resumen || {};
      setResumen({
        total: Number(srv.total || 0),
        activas: Number(srv.activas || 0),
        suspendidas: Number(srv.suspendidas || 0),
        finalizadas: Number(srv.finalizadas || 0),
        anuladas: Number(srv.anuladas || 0),
        pendientesPago: Number(srv.pendientesPago || 0),
        pagoParcial: Number(srv.pagoParcial || 0),
        pagadas: Number(srv.pagadas || 0),
      });

      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener las matrículas.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filtros]);

  useEffect(() => {
    fetchMatriculas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const prevFiltrosRef = useRef(filtros);
  useEffect(() => {
    if (prevFiltrosRef.current === filtros) return;
    prevFiltrosRef.current = filtros;
    setPage(1);
    fetchMatriculas(1, filtros);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchMatriculas();
    }, 350);
  }, [fetchMatriculas]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    socket.on("matriculas:actualizadas", scheduleRefresh);
    socket.on("pagos:actualizados", scheduleRefresh);

    return () => {
      socket.off("matriculas:actualizadas", scheduleRefresh);
      socket.off("pagos:actualizados", scheduleRefresh);
    };
  }, [scheduleRefresh]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const upsertMatricula = useCallback(() => {
    setPage(1);
    fetchMatriculas(1, filtros);
  }, [fetchMatriculas, filtros]);

  const removeMatricula = useCallback(() => {
    fetchMatriculas();
  }, [fetchMatriculas]);

  const seleccionar = useCallback(async (matricula) => {
    if (!matricula) {
      setSeleccionado(null);
      return;
    }
    const id = matricula.id || matricula;
    try {
      const detalle = await obtenerMatricula(id);
      setSeleccionado(normalizeMatricula(detalle));
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudo cargar la matrícula." });
    }
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    matriculas,
    matriculasFiltradas: matriculas,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    matriculasPaginadas: matriculas,
    reload: fetchMatriculas,
    upsertMatricula,
    removeMatricula,
    seleccionar,
  };
}
